import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { apiUrl } from '../../../../auth/api-url';
import {
  PricingSupplement,
  PricingSupplementLinkRequest,
  PricingSupplementMissingField,
  PricingSupplementStatus,
} from './pricing-supplement.types';

@Injectable({
  providedIn: 'root',
})
export class PricingSupplementService {
  private readonly endpoint = apiUrl('PricingSupplement');

  constructor(private readonly http: HttpClient) {}

  /** Upload eligibility (missing bond fields) and the current supplement. 404 means the trade does not exist. */
  getByTrade(tradeId: string): Observable<PricingSupplementStatus> {
    return this.http.get<unknown>(this.tradeUrl(tradeId)).pipe(
      map((response) => normalizePricingSupplementStatus(response, tradeId)),
    );
  }

  /** Publishes the PDF directly as Public in EzShare with the Deal ID as Identifier (400 if the trade has no Deal ID). */
  uploadPublic(tradeId: string, file: File): Observable<PricingSupplement> {
    const formData = new FormData();
    formData.append('File', file);

    return this.http.post<unknown>(`${this.tradeUrl(tradeId)}/public`, formData).pipe(
      map((response) => normalizePricingSupplement(response)),
    );
  }

  /** Links a document the user already uploaded in EzShare. */
  linkExisting(tradeId: string, request: PricingSupplementLinkRequest): Observable<PricingSupplement> {
    const body: PricingSupplementLinkRequest = { ezShareDocumentId: request.ezShareDocumentId.trim() };

    return this.http.put<unknown>(`${this.tradeUrl(tradeId)}/link`, body).pipe(
      map((response) => normalizePricingSupplement(response)),
    );
  }

  delete(tradeId: string): Observable<void> {
    return this.http.delete<void>(this.tradeUrl(tradeId));
  }

  private tradeUrl(tradeId: string): string {
    return `${this.endpoint}/trade/${encodeURIComponent(tradeId.trim())}`;
  }
}

export function normalizePricingSupplementStatus(response: unknown, tradeId = ''): PricingSupplementStatus {
  const record = asRecord(response);
  const rawSupplement = read(record, 'pricingSupplement');
  const pricingSupplement =
    typeof rawSupplement === 'object' && rawSupplement !== null ? normalizePricingSupplement(rawSupplement) : null;
  const rawMissing = read(record, 'missingFields');
  const missingFields: PricingSupplementMissingField[] = Array.isArray(rawMissing)
    ? rawMissing
        .map((item) => {
          const entry = asRecord(item);
          const field = readText(entry, 'field') ?? '';
          return { field, label: readText(entry, 'label') ?? field };
        })
        .filter((item) => item.label !== '')
    : [];
  const canUpload = read(record, 'canUploadPricingSupplement');

  return {
    tradeId: readText(record, 'tradeId') ?? tradeId,
    hasPricingSupplement: read(record, 'hasPricingSupplement') === true || pricingSupplement !== null,
    canUploadPricingSupplement: typeof canUpload === 'boolean' ? canUpload : missingFields.length === 0,
    missingFields,
    message: readText(record, 'message'),
    pricingSupplement,
  };
}

export function normalizePricingSupplement(response: unknown): PricingSupplement {
  const record = asRecord(response);

  return {
    tradeId: readText(record, 'tradeId') ?? '',
    ezShareDocumentId: readText(record, 'ezShareDocumentId') ?? '',
    internalLink: readText(record, 'internalLink') ?? '',
    publicLink: readText(record, 'publicLink') ?? '',
    fileName: readText(record, 'fileName'),
    createdBy: readText(record, 'createdBy'),
    dateCreated: readText(record, 'dateCreated'),
    modifiedBy: readText(record, 'modifiedBy'),
    dateModified: readText(record, 'dateModified'),
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
}

function read(record: Record<string, unknown>, camelKey: string): unknown {
  const pascalKey = camelKey.charAt(0).toUpperCase() + camelKey.slice(1);
  return record[camelKey] ?? record[pascalKey];
}

function readText(record: Record<string, unknown>, camelKey: string): string | null {
  const value = read(record, camelKey);
  if (value === null || value === undefined) {
    return null;
  }

  const text = String(value).trim();
  return text ? text : null;
}
